import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Customer, ENVIRONMENT, Environment } from '@netfoundry-ui/shared/model';
import { result, toLower } from 'lodash';
import { catchError, delay } from 'rxjs/operators';
import { throwError as observableThrowError } from 'rxjs/internal/observable/throwError';
import {
    GetOption,
    HateoasResourceOperation,
    HttpMethod,
    PagedGetOption,
    PagedResourceCollection,
} from '@lagoshny/ngx-hateoas-client';
import { take, tap } from 'rxjs/operators';
import { HTTP_CLIENT, TokenService } from './token.service';
import { LoggerService } from './logger.service';


@Injectable({ providedIn: 'root' })
export class CustomerService extends HateoasResourceOperation<Customer> {
    apiUrl;
    appsUrl;
    networkPricing = 199.0;
    regionPricing = [{ region: null, price: 0.2, rate: 'GB' }];
    lastPageCount = 0;
    lastTotalCount = 0;
    static defaultHttpAccept = {
        headers: { accept: 'application/hal+json' },
        pageParams: {
            page: 0,
            size: 2000,
        },
    };

    constructor(
        private logger: LoggerService,
        private tokenService: TokenService,
        @Inject(HTTP_CLIENT) private http: HttpClient,
        @Inject(ENVIRONMENT) private environment: Environment
    ) {
        super(Customer);
        this.apiUrl = this.environment.customerURL;
        this.appsUrl = this.environment.appsURL;

    }

    validateNewSignup(data) {
        const path = 'signups';
        const headers = new HttpHeaders().set('Accept', 'application/json').set('nf-validate', '');
        const fullpath = `${this.apiUrl}${path}`;
        const body = data;

        return this.http.post(fullpath, JSON.stringify(body), { headers: headers }).toPromise();
    }

   async validateSignUp(data, props: string = '') {
        const path = 'signups';
        const fullPath = `${this.apiUrl}${path}`;
        let headers = this.setHeaders();
        headers = headers.set('nf-validate', props);
        const options = {
            headers: headers,
        };
        return this.http
            .post(fullPath, data, options)
            .toPromise()
            .then((result: any) => result)
            .catch((result): any => false);
    }

    public findCustomers(options?: PagedGetOption): Promise<Customer[]> {
        return super
            .getPage({ ...CustomerService.defaultHttpAccept, ...options })
            .pipe(
                tap((val) => {
                    this.lastPageCount = val.totalPages;
                    this.lastTotalCount = val.totalElements;
                })
            )
            .toPromise()
            .then((customers) => (customers ? customers.resources : []));
    }

    findCustomersProfiles() {
        const headers = new HttpHeaders().set('Accept', 'application/json').set('nf-validate', '');
        return this.http
            .get(this.apiUrl + `customers?enabledForOnboarding=true`, {
                headers: headers,
                responseType: 'json',
            })
            .toPromise();
    }

    public findCustomer(customerId: string) {
        const fullpath = `${this.apiUrl}customers/${customerId}`;
        return this.http
            .get(fullpath)
    }

    public create(model, parentCustomerId) {
        const fullpath = `${this.apiUrl}/customers/${parentCustomerId}/onboard`;
            return this.http
                .post(fullpath, model)
    }

    public offboard(id) {
        const fullpath = `${this.apiUrl}/customers/${id}/offboard`;
            return this.http
                .put(fullpath, {})
    }

    public validateCreate(parentCustomerId, model) {
        const headers = new HttpHeaders().set('Accept', 'application/json').set('nf-validate', '');
        return this.http
            .post(this.apiUrl + `customers/${parentCustomerId}/onboard`, model, {
                headers: headers,
                responseType: 'json',
            })
            .toPromise();
    }

    public enabledProfiles(parentCustomerId) {
        const headers = new HttpHeaders().set('Accept', 'application/json').set('nf-validate', '');
        return this.http
            .get(`https://` + this.environment.gatewayUrl + `/customer-profiles/${parentCustomerId}/`, {
                headers: headers,
                responseType: 'json',
            })
            .toPromise();
    }


    public update(id, model) {
        const headers = this.setHeaders();
        const fullpath = `${this.apiUrl}/oidc-audiences/${id}`;
            return this.http
                .patch(fullpath, model)
      
    }

    public delete(id: string) {
        const headers = this.setHeaders();
        const fullpath = `${this.apiUrl}/customers/${id}`;
            return this.http
                .delete(fullpath, {headers: headers})
    }

    createNewSignUp(data: any) {
        const path = 'signups';
        const fullPath = `${this.apiUrl}${path}`;
            return this.http
            .post(fullPath, data)
            .toPromise()
            .then((result: any) => {
                this.logger.log(result);
                return result;
            });
    }


    getSignUp(signupId) {
        const path = `signups/${signupId}`;
        return this.get(path)
            .toPromise()
            .then((result: any) => {
                this.logger.log(result);
                return result;
            });
    }

    selfResume() {
        const path = `customers/self/resume`;
        return this.put(path, {})
            .toPromise()
            .then((result: any) => {
                this.logger.log(result);
                return result;
            });
    }

    submitHubspotEntries(entries: any = []) {
        const fullpath = `${this.appsUrl}survey`;

        const body = {};
        entries.forEach((entry: any) => {
            body[entry.key] = entry.value;
        });
        return this.http.post(fullpath, JSON.stringify(body), { headers: this.setHeaders() });
    }

    requestSelfOffboard() {
        const path = '/customers/self/offboard';
        return this.post(path, {})
            .toPromise()
            .then((result: any) => {
                this.logger.log(result);
                return result;
            });
    }

    getCountriesList() {
        const path = 'countries';
        return this.get(path)
            .toPromise()
            .then((result: any) => {
                this.logger.log(result);
                return result;
            });
    }

    setNewSelfServiceUserFlags(tenantLabel, zipcode = '', country = '', plan = '') {
        localStorage.setItem(`nf_new_self_service_${tenantLabel}`, 'true');
        localStorage.setItem(`showGettingStarted_${tenantLabel}`, 'true');
        localStorage.setItem(`showSurvey_${tenantLabel}`, 'true');
        localStorage.setItem(`createNetwork_${tenantLabel}`, 'true');
        localStorage.setItem(`createFabric_${tenantLabel}`, 'true');
        localStorage.setItem(`nf_show_quick_start_hint_${tenantLabel}`, 'true');
        localStorage.setItem(`show_endpoints_info_${tenantLabel}`, 'true');
        localStorage.setItem(`show_services_info_${tenantLabel}`, 'true');
        localStorage.setItem(`show_edge-routers_info_${tenantLabel}`, 'true');
        localStorage.setItem(`show_appwans_info_${tenantLabel}`, 'true');
        localStorage.setItem(`show_posture-checks_info_${tenantLabel}`, 'true');
        localStorage.setItem(`show_edge-router-policies_info_${tenantLabel}`, 'true');
        localStorage.setItem(`show_certificates_info_${tenantLabel}`, 'true');
        localStorage.setItem(`fabricZipcode_${tenantLabel}`, zipcode);
        localStorage.setItem(`fabricCountry_${tenantLabel}`, country);
        localStorage.removeItem(`newInvitedUser_${tenantLabel}`);
        if (toLower(plan) === 'teams') {
            localStorage.setItem(`isSelfServiceTeams_${tenantLabel}`, 'true');
        }
    }

    /**
     * @param path
     * @param data
     */
    public post(path: string, data) {
        const fullpath = `${this.apiUrl}${path}`;
        const body = data;

        return this.http.post(fullpath, JSON.stringify(body), { headers: this.setHeaders() });
    }

    /**
     * @param path
     * @param data
     */
    public put(path: string, data) {
        const fullpath = `${this.apiUrl}${path}`;
        const body = data;

        return this.http.put(fullpath, JSON.stringify(body), { headers: this.setHeaders() });
    }

    /**
     * Generic GETter
     */

    public get(path: string) {
        const fullpath = `${this.apiUrl}${path}`;

        return this.http.get(fullpath, { headers: this.setHeaders() });
    }

    /**
     * Return headers
     */
    private setHeaders(): HttpHeaders {
        const headers = new HttpHeaders();

                  return headers.append('Content-Type', 'application/json');
    }

        /**
     * Generic error handler for bad requests
     */
        protected handleError(scope: any, error: HttpErrorResponse) {
            if (error.error instanceof ErrorEvent) {
                // A client-side or network error occurred. Handle it accordingly.
                scope.logger.error('Feature Service error occurred:', error.error);
            } else {
                // The backend returned an unsuccessful response code.
                // The response body may contain clues as to what went wrong,
                scope.logger.error(`Feature Backend returned code ${error.status}`, error.error);
            }
            scope.lastErrorSource.next(error);
            return observableThrowError(error);
        }
}
